import { create } from 'zustand';
import {
  USER_ROLE_CAPABILITIES,
  type UserRoleCapability,
  type UserRoleCapabilityMap,
  type UserRoleCapabilityInput,
  type UserRole,
  type UserRoleInput,
} from '@api/services/user-role-capabilities/types';

type UserRoleCapabilitiesState = {
  role: UserRole | null;
  roleCapabilities: UserRoleCapabilityMap;
  hasLoadedRoleCapabilities: boolean;

  setUserRoleCapabilities: (role: UserRoleInput, capabilities?: UserRoleCapabilityInput[] | null) => void;
  hasRoleCapability: (capability: UserRoleCapability) => boolean;
  clearUserRoleCapabilities: () => void;
};

const defaultRoleCapabilities: UserRoleCapabilityMap = {
  createRequest: false,
  editRequest: false,
  signSubmitRequest: false,
  withdrawRequest: false,
  cancelRequest: false,
  manageAccess: false,
  viewDashboard: false,
  viewDeveloperTab: false,
  processHelpInquiry: false,
};

export const mapApiRoleToUserRole = (role: UserRoleInput): UserRole | null => {
  if (!role) return null;

  const userRoleKey = role.trim().toLowerCase().replace(/\s+/g, '');

  const userRoleMap: Record<string, UserRole> = {
    applicant: 'applicant',
    agent: 'agent',
    usaceregulator: 'usaceRegulator',
    helpdesk: 'helpDesk',
    externalagency: 'externalAgency',
  };

  return userRoleMap[userRoleKey] ?? null;
};

const getUserCapabilities = (capabilities?: UserRoleCapabilityInput[] | null): UserRoleCapabilityMap => {
  const enabledCapabilities = new Set(capabilities ?? []);
  const capabilityMap: UserRoleCapabilityMap = { ...defaultRoleCapabilities };

  USER_ROLE_CAPABILITIES.forEach((capability) => {
    capabilityMap[capability] = enabledCapabilities.has(capability);
  });

  return capabilityMap;
};

export const useUserRoleCapabilities = create<UserRoleCapabilitiesState>((set, get) => ({
  role: null,
  roleCapabilities: { ...defaultRoleCapabilities },
  hasLoadedRoleCapabilities: false,

  setUserRoleCapabilities: (role, capabilities) => {
    const userRole = mapApiRoleToUserRole(role);

    set({
      role: userRole,
      roleCapabilities: getUserCapabilities(capabilities),
      hasLoadedRoleCapabilities: true,
    });
  },

  hasRoleCapability: (capability) => Boolean(get().roleCapabilities[capability]),

  clearUserRoleCapabilities: () =>
    set({
      role: null,
      roleCapabilities: { ...defaultRoleCapabilities },
      hasLoadedRoleCapabilities: false,
    }),
}));
